// Copyright (c) 2026 Michael F. Kamprath
//
// plaque-animators.js -- Entrance animators for number plaques in
// Learn mode.
//
// A plaque container (built by createNumberPlaque) sits at the
// theme's `centerPoint` relative to the sprite container.  When a
// child taps an object, the count increments and the plaque enters
// with one of three animations chosen by `numberPlaqueConfig.animator.type`:
//
//   zoomOut           -- starts at startPoint scaled to 0.01, grows to
//                        finalPosition at scale 1.0 over animationTime.
//                        Optional fadeInTime pumps alpha 0 -> 1 in
//                        parallel.  Used by chicks and horses.
//
//   zoomRotate        -- like zoomOut but starts at scale 0.8 with a
//                        rotation of startAngle degrees, lerps to
//                        scale 1.0 / 0 degrees.  Used by cows.
//
//   animationSequence -- hides the numeral text, swaps the plaque
//                        sprite's texture through a keyframe cycle
//                        (puff, wormPop, flowerPlaqueGrow), optionally
//                        fires a sound at start, then reveals the
//                        numeral.  Used by sheep, apples, flowers.
//
// Coordinate system: `finalX`/`finalY` is where the plaque container
// sits at rest in its parent (sprite) container.  startPoint in the
// animator config is in plaque-image coords (same frame as
// numberFrame, measured from the plaque image's top-left).  The
// translation from "image-coord startPoint" to "container offset" is
// `(startPoint - imageCenter) * plaqueDisplayScale`.
//
// All animators honour an optional cancellation token
// `{ cancelled: boolean }` so scene teardown can stop them mid-flight.

import { animateOver, sampleCycle, cycleNaturalDuration } from './entrance.js';
import { plaqueDisplayScale } from './number-plaque.js';
import { play as playOneShot } from '../audio/engine.js';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Compute where a zoomOut/zoomRotate plaque container starts, given
 * the animator's startPoint (in plaque-image coords) and the resting
 * finalX/finalY (in sprite container coords).
 *
 * Returns the resting point itself if startPoint is missing.
 *
 * @param {object} cfg     The theme's numberPlaqueConfig.
 * @param {number} finalX  Resting container x (in sprite coords).
 * @param {number} finalY  Resting container y (in sprite coords).
 * @returns {{ x: number, y: number }}
 */
export function computeStartPosition(cfg, finalX, finalY) {
  const sp = cfg?.animator?.startPoint;
  if (!sp || !cfg?.imageSize) return { x: finalX, y: finalY };
  const scale = plaqueDisplayScale(cfg);
  const imgCx = cfg.imageSize.w / 2;
  const imgCy = cfg.imageSize.h / 2;
  return {
    x: finalX + (sp.x - imgCx) * scale,
    y: finalY + (sp.y - imgCy) * scale,
  };
}

/**
 * Resolve a keyframe image path to a PIXI Texture using the supplied
 * texture map.  Returns null when missing — the caller is expected to
 * skip the frame so the previous texture stays on screen.
 *
 * @param {string} imagePath
 * @param {Map|Object|null} textureMap
 * @returns {*}
 */
function resolveTexture(imagePath, textureMap) {
  if (!imagePath || !textureMap) return null;
  if (typeof textureMap.get === 'function') return textureMap.get(imagePath) || null;
  return textureMap[imagePath] || null;
}

/**
 * Snap a plaque container to its resting position, opaque, unscaled,
 * unrotated.  Used as the default "no animator" path and as the
 * end-of-animation cleanup.
 */
function snapToFinal(plaqueContainer, finalX, finalY) {
  plaqueContainer.visible = true;
  plaqueContainer.alpha = 1;
  plaqueContainer.x = finalX;
  plaqueContainer.y = finalY;
  if (plaqueContainer.scale && typeof plaqueContainer.scale.set === 'function') {
    plaqueContainer.scale.set(1, 1);
  }
  plaqueContainer.rotation = 0;
}

// ---------------------------------------------------------------------------
// Asset preloading
// ---------------------------------------------------------------------------

/**
 * Collect every image and sound URL referenced by the plaque entrance
 * animators across all themes, so the caller can preload them up front.
 *
 * Static plaque images (numberPlaqueConfig.image) are NOT included --
 * those are handled by preloadPlaqueTextures in number-plaque.js.
 *
 * @param {object} themes              Parsed themes.json root.
 * @param {object} plaqueAnimations    The plaqueAnimations block from
 *                                     animations.json.
 * @returns {{ images: string[], sounds: string[] }}
 */
export function collectPlaqueAnimatorAssets(themes, plaqueAnimations) {
  const images = new Set();
  const sounds = new Set();
  for (const theme of Object.values(themes || {})) {
    const anim = theme?.numberPlaqueConfig?.animator;
    if (!anim) continue;
    if (anim.sound) sounds.add(anim.sound);
    if (anim.type === 'animationSequence' && anim.animationKey) {
      const frames = plaqueAnimations?.[anim.animationKey];
      if (Array.isArray(frames)) {
        for (const k of frames) {
          if (k.image) images.add(k.image);
          if (k.sound) sounds.add(k.sound);
        }
      }
    }
  }
  return { images: [...images], sounds: [...sounds] };
}

/**
 * Collect every image and sound URL referenced by a theme's
 * `plaqueReaction` block -- the object-side animation that plays IN
 * PARALLEL with the plaque reveal (e.g. apples' `worriedApple` face
 * sequence).
 *
 * Keyframe images and `soundFile` entries are included so they can be
 * preloaded with the rest of the plaque assets.  The `finalStillImage`
 * is also added so learn-tap can set the sprite's resting texture
 * after the reaction completes without an extra fetch.
 *
 * @param {object} themes          Parsed themes.json root.
 * @param {object} objectReactions The objectReactions block from
 *                                 animations.json.
 * @returns {{ images: string[], sounds: string[] }}
 */
export function collectPlaqueReactionAssets(themes, objectReactions) {
  const images = new Set();
  const sounds = new Set();
  for (const theme of Object.values(themes || {})) {
    const pr = theme?.plaqueReaction;
    if (!pr) continue;
    if (pr.finalStillImage) images.add(pr.finalStillImage);
    const frames = objectReactions?.[pr.animationKey];
    if (!Array.isArray(frames)) continue;
    for (const k of frames) {
      if (k.image) images.add(k.image);
      if (k.soundFile) sounds.add(k.soundFile);
      if (Array.isArray(k.soundFileList)) {
        for (const s of k.soundFileList) sounds.add(s);
      }
    }
  }
  return { images: [...images], sounds: [...sounds] };
}

// ---------------------------------------------------------------------------
// Individual animators
// ---------------------------------------------------------------------------

/**
 * Common shape passed to every animator.
 *
 * @typedef {object} AnimatorOptions
 * @property {object}  cfg            The theme's numberPlaqueConfig.
 * @property {number}  finalX         Resting container x (sprite coords).
 * @property {number}  finalY         Resting container y (sprite coords).
 * @property {object}  [plaqueAnimations]  animations.json plaqueAnimations
 *                                         block (animationSequence only).
 * @property {Map|Object} [textureMap]    Pre-loaded keyframe textures
 *                                         (animationSequence only).
 * @property {{ cancelled: boolean }} [cancel]  Cancellation token.
 */

/**
 * Plaque enters by zooming out from a tiny start point to its resting
 * position.  Position and scale animate in parallel; alpha optionally
 * fades in over fadeInTime.
 *
 * @param {*} plaqueContainer
 * @param {AnimatorOptions} opts
 * @returns {Promise<void>}
 */
export async function animateZoomOut(plaqueContainer, opts) {
  const { cfg, finalX, finalY, cancel } = opts;
  const anim = cfg?.animator || {};
  const durationMs = (anim.animationTime || 0.1) * 1000;
  const fadeInMs = (anim.fadeInTime || 0) * 1000;
  const startScale = 0.01;
  const endScale = 1.0;
  const { x: startX, y: startY } = computeStartPosition(cfg, finalX, finalY);

  plaqueContainer.visible = true;
  plaqueContainer.alpha = fadeInMs > 0 ? 0 : 1;
  plaqueContainer.x = startX;
  plaqueContainer.y = startY;
  if (plaqueContainer.scale?.set) plaqueContainer.scale.set(startScale, startScale);
  plaqueContainer.rotation = 0;

  const fadePromise = fadeInMs > 0
    ? animateOver(fadeInMs, (t) => { plaqueContainer.alpha = t; }, cancel)
    : Promise.resolve();

  const movePromise = animateOver(durationMs, (t) => {
    plaqueContainer.x = startX + (finalX - startX) * t;
    plaqueContainer.y = startY + (finalY - startY) * t;
    const s = startScale + (endScale - startScale) * t;
    if (plaqueContainer.scale?.set) plaqueContainer.scale.set(s, s);
  }, cancel);

  await Promise.all([fadePromise, movePromise]);
  snapToFinal(plaqueContainer, finalX, finalY);
}

/**
 * Plaque enters by zooming + rotating from startPoint at scale 0.8 to
 * finalPosition at scale 1.0 / 0 degrees.  startAngle is in degrees.
 *
 * @param {*} plaqueContainer
 * @param {AnimatorOptions} opts
 * @returns {Promise<void>}
 */
export async function animateZoomRotate(plaqueContainer, opts) {
  const { cfg, finalX, finalY, cancel } = opts;
  const anim = cfg?.animator || {};
  const durationMs = (anim.animationTime || 0.1) * 1000;
  const fadeInMs = (anim.fadeInTime || 0) * 1000;
  const startScale = 0.8;
  const endScale = 1.0;
  const startAngleRad = ((anim.startAngle || 0) * Math.PI) / 180;
  const { x: startX, y: startY } = computeStartPosition(cfg, finalX, finalY);

  plaqueContainer.visible = true;
  plaqueContainer.alpha = fadeInMs > 0 ? 0 : 1;
  plaqueContainer.x = startX;
  plaqueContainer.y = startY;
  if (plaqueContainer.scale?.set) plaqueContainer.scale.set(startScale, startScale);
  plaqueContainer.rotation = startAngleRad;

  const fadePromise = fadeInMs > 0
    ? animateOver(fadeInMs, (t) => { plaqueContainer.alpha = t; }, cancel)
    : Promise.resolve();

  const movePromise = animateOver(durationMs, (t) => {
    plaqueContainer.x = startX + (finalX - startX) * t;
    plaqueContainer.y = startY + (finalY - startY) * t;
    const s = startScale + (endScale - startScale) * t;
    if (plaqueContainer.scale?.set) plaqueContainer.scale.set(s, s);
    plaqueContainer.rotation = startAngleRad * (1 - t);
  }, cancel);

  await Promise.all([fadePromise, movePromise]);
  snapToFinal(plaqueContainer, finalX, finalY);
}

/**
 * Plaque enters as a keyframe-driven texture-swap animation on the
 * plaque's background sprite.  The numeral text is hidden during the
 * sequence and revealed when it completes.  An optional `sound` URL
 * fires once at the start.  deltaX/deltaY tracks shift the container
 * horizontally/vertically over the cycle (used by flowerPlaqueGrow to
 * slide the plaque from offset back to centre).
 *
 * @param {*} plaqueContainer
 * @param {AnimatorOptions} opts
 * @returns {Promise<void>}
 */
export async function animateAnimationSequence(plaqueContainer, opts) {
  const { cfg, finalX, finalY, cancel, plaqueAnimations, textureMap } = opts;
  const anim = cfg?.animator || {};
  const animationKey = anim.animationKey;
  const keyframes = (plaqueAnimations && plaqueAnimations[animationKey]) || [];

  // Snap container to resting transform so the keyframe sequence plays
  // in place.  deltaX/deltaY will offset from this baseline per-frame.
  snapToFinal(plaqueContainer, finalX, finalY);

  const sprite = plaqueContainer._plaqueSprite;
  const textNode = plaqueContainer._plaqueText;
  if (textNode) textNode.visible = false;

  // Fire the entrance sound -- once, at the start.  Missing sound is
  // not an error: the legacy app's apple/flower sequences omit it.
  if (anim.sound) {
    try { playOneShot('/' + anim.sound); } catch { /* never block animation on audio errors */ }
  }

  if (keyframes.length > 0 && sprite) {
    const cycleTime = cycleNaturalDuration(keyframes);
    const durationMs = cycleTime * 1000;
    const scale = plaqueDisplayScale(cfg);
    let lastImage = null;

    await animateOver(durationMs, (t) => {
      // Clamp to the last sample at t=1 so the final keyframe renders
      // (sampleCycle wraps to t=0 if we passed exactly cycleTime).
      const cycleT = t >= 1 ? cycleTime : t * cycleTime;
      const sample = sampleCycle(keyframes, cycleT, cycleTime);

      // Texture swap (guarded so we don't replace a present texture
      // with a missing one and erase the visible plaque).
      if (sample.image && sample.image !== lastImage) {
        const tex = resolveTexture(sample.image, textureMap);
        if (tex) {
          sprite.texture = tex;
          lastImage = sample.image;
        }
      }

      // deltaX/Y shift the entire plaque container relative to its
      // resting finalX/finalY.  Multiply by plaqueDisplayScale so a
      // theme that's clamped via maxHeight still moves at the same
      // visual ratio.
      plaqueContainer.x = finalX + sample.deltaX * scale;
      plaqueContainer.y = finalY + sample.deltaY * scale;
    }, cancel);
  }

  // Reveal the numeral and snap back to the resting transform.
  if (textNode) textNode.visible = true;
  snapToFinal(plaqueContainer, finalX, finalY);
}

// ---------------------------------------------------------------------------
// Dispatcher
// ---------------------------------------------------------------------------

/**
 * Run the entrance animation for a plaque container, dispatching on
 * the animator type declared in the theme's numberPlaqueConfig.  If
 * no animator (or an unknown type) is configured, the plaque snaps to
 * its resting position immediately.
 *
 * @param {*} plaqueContainer
 * @param {AnimatorOptions} opts
 * @returns {Promise<void>}
 */
export async function animatePlaqueEntrance(plaqueContainer, opts) {
  const type = opts?.cfg?.animator?.type;
  switch (type) {
    case 'zoomOut':
      return animateZoomOut(plaqueContainer, opts);
    case 'zoomRotate':
      return animateZoomRotate(plaqueContainer, opts);
    case 'animationSequence':
      return animateAnimationSequence(plaqueContainer, opts);
    default:
      snapToFinal(plaqueContainer, opts.finalX, opts.finalY);
      return;
  }
}

// ---------------------------------------------------------------------------
// Plaque reaction (object-side animation, runs parallel to plaque reveal)
// ---------------------------------------------------------------------------

/**
 * Play a keyframe-driven reaction on the SPRITE (not the plaque)
 * alongside the plaque reveal.  Mirrors the legacy
 * `displayPlaqueReactionConfig` coordination in CountableObjectView:
 * when plaqueSequence=simultaneous the reaction animation and the
 * plaque entrance run at the same time, and the sprite's resting
 * texture is swapped to `finalStillImage` after the reaction ends.
 *
 * Currently used only by the apples theme (worriedApple: the apple
 * grows a worried face in three steps as the worm-plaque pops out of
 * it).  The animation drives the `_inner` sprite's texture and a
 * single per-frame `soundFile` if authored.
 *
 * Cancellation: the cancel token is checked on every frame.  If
 * cancelled mid-flight the function returns immediately without
 * setting the finalStillImage — the scene teardown path is
 * responsible for any residual cleanup.
 *
 * @param {object} sprite               The scene's object container
 *                                      (has _inner sprite).
 * @param {{
 *   reactionCfg: object,               Theme's plaqueReaction block.
 *   objectReactions: object,           animations.json objectReactions.
 *   reactionTextureMap: Map|Object,    { imagePath -> Texture } lookup.
 *   cancel?: { cancelled: boolean },
 * }} opts
 * @returns {Promise<void>}
 */
export async function animatePlaqueReaction(sprite, opts) {
  const { reactionCfg, objectReactions, reactionTextureMap, cancel } = opts || {};
  if (!reactionCfg || !sprite) return;
  const inner = sprite._inner;
  if (!inner) return;

  const keyframes = objectReactions?.[reactionCfg.animationKey];
  const firedSounds = new Set();

  if (Array.isArray(keyframes) && keyframes.length > 0) {
    const cycleTime = cycleNaturalDuration(keyframes);
    const durationMs = cycleTime * 1000;
    let lastImage = null;

    await animateOver(durationMs, (t) => {
      const cycleT = t >= 1 ? cycleTime : t * cycleTime;
      const sample = sampleCycle(keyframes, cycleT, cycleTime);

      // Texture swap, guarded so a missing texture doesn't erase the
      // current one.
      if (sample.image && sample.image !== lastImage) {
        const tex = resolveTexture(sample.image, reactionTextureMap);
        if (tex) {
          inner.texture = tex;
          lastImage = sample.image;
        }
      }

      // Per-frame sound: fire once when the cycle-time crosses the
      // keyframe's authored t.  Fires as an opaque URL matching
      // the rest of the engine (leading slash).
      for (const k of keyframes) {
        if (!k.soundFile) continue;
        const key = String(k.t);
        if (firedSounds.has(key)) continue;
        if (cycleT >= k.t) {
          firedSounds.add(key);
          try { playOneShot('/' + k.soundFile); } catch { /* audio errors never block */ }
        }
      }
    }, cancel);
  }

  if (cancel?.cancelled) return;

  // Swap to the finalStillImage so the apple stays on its worried
  // face for the remainder of the scene.  Missing texture leaves the
  // last keyframe image in place -- no worse than before.
  if (reactionCfg.finalStillImage) {
    const finalTex = resolveTexture(reactionCfg.finalStillImage, reactionTextureMap);
    if (finalTex) inner.texture = finalTex;
  }
}
